import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { useInView } from "react-intersection-observer";
import confetti from "canvas-confetti";
import {
  Briefcase,
  Users,
  Building,
  MapPin,
  Calendar,
  ArrowRight,
  ChevronRight,
  CheckCircle,
  Target,
  Mail,
  Clock,
  ChevronDown,
  Eye,
  UserCheck,
Phone,
  HeartHandshake,
  User,
  Globe,
  Sparkles,
  Rocket,
  Trophy,
  GraduationCap,
  Zap,
  Heart,
  Gift,
  TrendingUp,
  ChevronLeft,
  Search,
  FileText,
  ShieldCheck,
  Star,
  Crown,
  BookOpen,
  List,
  FolderOpen,
  UserPlus,
  Lightbulb,
  Plus,
  PlusCircle,
  Circle,
  ClipboardList,
  HelpCircle,
  CalendarPlus,
  CalendarDays,
  Book,
  X,
  MessageCircle,
  Share2,
  Info,
  Building2
} from "lucide-react";
import { parseISO, formatDistanceToNow, format as formatDate } from "date-fns";
import COMPANY_LOGOS from "../constants/companyLogos";
import { format } from "date-fns";
import hero from "../components/SVKM-Home-Slide-1.jpg"
import hero2 from "../components/SVKM-Home-Slide-2.jpg"
import Navbar from "../components/AlumniNavbar";
import Footer from "../components/Footer";

// Utility Components
const HeroSection = ({ userData }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const images = [hero, hero2];

  useEffect(() => {
    setIsVisible(true);
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev === 0 ? 1 : 0));
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    { 
      icon: Users, 
      label: "Alumni", 
      value: userData.college.stats?.totalAlumni?.toLocaleString() || "10K+",
      gradient: "from-blue-500 to-indigo-500"
    },
    { 
      icon: Building2, 
      label: "Companies", 
      value: userData.college.stats?.totalCompanies?.toLocaleString() || "500+",
      gradient: "from-purple-500 to-pink-500"
    },
    { 
      icon: MapPin, 
      label: "Countries", 
      value: userData.college.stats?.countries?.toLocaleString() || "50+",
      gradient: "from-red-500 to-pink-500"
    },
    { 
      icon: Briefcase, 
      label: "Active Jobs", 
      value: userData.college.stats?.activeJobs?.toLocaleString() || "100+",
      gradient: "from-emerald-500 to-green-500"
    }
  ];

  const achievements = [
    { label: "Best College 2024", icon: Trophy, delay: 0.8 },
    { label: "100% Placement", icon: Target, delay: 1.0 },

  ];

  return (
    <section className="relative min-h-screen w-full overflow-hidden">
      {/* Hero Background Images with Parallax and Crossfade */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImage}
            src={images[currentImage]}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            alt="Campus"
            className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          />
        </AnimatePresence>

        {/* Enhanced Overlay Layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/40 to-indigo-900/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.15),transparent_70%)]" />
        
        {/* Animated Patterns */}
        <div className="absolute inset-0">
          <svg className="absolute inset-0 w-full h-full opacity-[0.15]">
            <defs>
              <pattern id="hero-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0L0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
              <pattern id="hero-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="currentColor" fillOpacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
            <rect width="100%" height="100%" fill="url(#hero-dots)" />
          </svg>
        </div>

        {/* Dynamic Light Effects */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/30 rounded-full filter blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3] 
            }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          />
          <motion.div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/30 rounded-full filter blur-3xl"
            animate={{ 
              scale: [1.2, 1, 1.2],
              opacity: [0.5, 0.3, 0.5] 
            }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2 
            }}
          />
        </div>
      </div>

      {/* Main Content Container */}
      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-8xl mx-auto w-full py-12 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-0 items-center">
            {/* Left Content - Text and CTA */}
            <motion.div 
              className="text-center lg:text-left lg:pr-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
              transition={{ duration: 0.8 }}
            >
              {/* Premium Welcome Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.2 }}
                className="inline-block mb-6"
              >
                <span className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 
                  border border-blue-500/30 text-blue-200 text-sm font-medium backdrop-blur-sm
                  shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                  Welcome to Your Alumni Network
                </span>
              </motion.div>

              {/* Enhanced Logo and College Name */}
              <div className="flex items-center justify-center lg:justify-start mb-8 space-x-6">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.8 }}
                  transition={{ delay: 0.4 }}
                  className="relative group"
                >
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl 
                    opacity-75 group-hover:opacity-100 blur transition duration-1000 group-hover:duration-200" />
                  <div className="relative w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br 
                    from-white/10 to-white/5 backdrop-blur-xl p-3 border border-white/20 shadow-2xl">
                    <img
                      src={userData.college.logo}
                      alt={userData.college.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
                  transition={{ delay: 0.5 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold"
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r 
                    from-white via-blue-100 to-blue-200">
                    {userData.college.name}
                  </span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.6 }}
                className="text-lg md:text-xl text-blue-100/90 mb-8 max-w-2xl mx-auto lg:mx-0"
              >
                Join our thriving community of {userData.college.stats?.totalAlumni?.toLocaleString()}+ alumni 
                in {userData.college.address?.city}, where connections become opportunities and 
                memories turn into lifelong bonds.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap justify-center lg:justify-start gap-4"
              >
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(37,99,235,0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/directory')}
                  className="px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 
                    hover:from-blue-700 hover:to-indigo-700 text-white font-medium shadow-lg 
                    shadow-blue-500/25 flex items-center space-x-3 group"
                >
                  <Globe className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">
                    Explore Network
                  </span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/register')}
                  className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-white 
                    font-medium backdrop-blur-sm border border-white/10 flex items-center space-x-3 
                    transition-all group"
                >
                  <UserPlus className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                  <span>Join Now</span>
                </motion.button>
              </motion.div>

              {/* Stats Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.8 }}
                className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4"
              >
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.1)' }}
                    className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 
                      transition-all duration-300"
                  >
                    <stat.icon className={`w-6 h-6 text-blue-400 mb-2`} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-blue-200">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Content - Featured Image/Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="relative hidden lg:block"
            >
              {/* Featured Image Container */}
              <div className="relative aspect-square max-w-xl mx-auto">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 
                  rounded-full blur-3xl opacity-50 animate-pulse" />
                
                {/* Campus Image */}
                <div className="relative rounded-3xl overflow-hidden border border-white/10 
                  shadow-2xl shadow-black/50">
                  <motion.img
                    src={images[currentImage]}
                    alt="Campus Life"
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </div>

                {/* Floating Achievement Cards */}
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50, y: 20 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ delay: achievement.delay }}
                    className={`absolute ${
                      index === 0 ? 'top-10 -left-10' :
                      index === 1 ? 'top-1/2 -right-10' :
                      'bottom-10 -left-10'
                    } bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 
                    shadow-lg`}
                  >
                    <div className="flex items-center space-x-3">
                      <achievement.icon className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium">{achievement.label}</span>
                    </div>
                  </motion.div>
                ))}
                {/* Interactive Elements Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Animated Particles */}
                  <motion.div
                    className="absolute w-2 h-2 rounded-full bg-blue-400"
                    animate={{
                      scale: [1, 2, 1],
                      opacity: [0.5, 0, 0.5],
                      y: [-10, -50],
                      x: [-10, 10]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <motion.div
                    className="absolute right-1/4 bottom-1/4 w-2 h-2 rounded-full bg-indigo-400"
                    animate={{
                      scale: [1, 2, 1],
                      opacity: [0.5, 0, 0.5],
                      y: [10, -30],
                      x: [10, -10]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1
                    }}
                  />
                </div>

                {/* Interactive Hover Effects */}
                
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block" // Added hidden lg:block
        >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className=" w-6 h-10 rounded-full border-2 border-white/40 flex justify-center p-2
            backdrop-blur-sm bg-white/5 group hover:border-white/60 hover:bg-white/10 
            transition-all duration-300"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1 h-1 rounded-full bg-white group-hover:bg-blue-400 
              transition-colors duration-300"
          />
        </motion.div>
        <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs 
          text-white/60 whitespace-nowrap">Scroll to explore</span>
      </motion.div>

      {/* Image Navigation Controls */}
      <div className="absolute bottom-8 right-8 flex-col items-end space-y-4 hidden lg:flex"> {/* Changed flex to hidden lg:flex */}
      {/* Slide Counter */}
        <div className="text-white/80 font-medium backdrop-blur-sm bg-white/5 px-3 py-1 rounded-full
          border border-white/10">
          <span className="text-blue-400">{currentImage + 1}</span>
          <span className="mx-1">/</span>
          <span>{images.length}</span>
        </div>

        {/* Navigation Dots */}
        <div className="flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              className={`transition-all duration-300 ${
                currentImage === index 
                  ? "w-8 h-2 bg-blue-400" 
                  : "w-2 h-2 bg-white/50 hover:bg-white/75"
              } rounded-full`}
            />
          ))}
        </div>
      </div>

      {/* Page Section Navigation */}
      <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
        <div className="flex flex-col space-y-4">
          {["Home", "About", "Network", "Events"].map((section, index) => (
            <motion.button
              key={section}
              whileHover={{ x: -5 }}
              className={`w-2 h-2 rounded-full ${
                index === 0 
                  ? "w-4 bg-blue-400" 
                  : "bg-white/50 hover:bg-white/75"
              } transition-all duration-300`}
              title={section}
            />
          ))}
        </div>
      </div>

     

     
    </section>
  );
};


const LoadingAnimation = () => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative mb-8"
      >
        <div className="w-24 h-24 rounded-full border-4 border-blue-200 animate-pulse" />
        <div className="w-24 h-24 rounded-full border-4 border-blue-500 border-t-transparent animate-spin absolute top-0" />
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <GraduationCap className="w-10 h-10 text-blue-500" />
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center"
      >
        <h2 className="text-2xl font-semibold text-blue-900 mb-2">
          Loading Saarthi
        </h2>
        <p className="text-blue-600">Connecting alumni worldwide...</p>
      </motion.div>
    </div>
  );
};

// Geometric Background Patterns
const GeometricPatterns = () => {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-[0.15]">
      <defs>
        <pattern
          id="dot-pattern"
          x="0"
          y="0"
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="2" cy="2" r="1" fill="currentColor" />
        </pattern>
        <pattern
          id="grid-pattern"
          x="0"
          y="0"
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 40 0 L 0 0 0 40"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </pattern>
        <pattern
          id="wave-pattern"
          x="0"
          y="0"
          width="100"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 0 10 Q 25 20, 50 10 T 100 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#dot-pattern)" />
      <rect width="100%" height="100%" fill="url(#grid-pattern)" />
      <rect width="100%" height="100%" fill="url(#wave-pattern)" />
    </svg>
  );
};

// Section Header Component
const SectionHeader = ({ title, subtitle, alignment = "center" }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className={`mb-12 ${alignment === "center" ? "text-center" : ""}`}
    >
      <motion.h2
        className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {title}
      </motion.h2>
      <motion.p
        className="text-xl text-gray-600 max-w-3xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {subtitle}
      </motion.p>
    </motion.div>
  );
};

const EnhancedCarousel = ({ items, renderItem, title, subtitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef(null);

  const itemsPerView = useCallback(() => {
    if (typeof window === "undefined") return 3;
    if (window.innerWidth >= 1280) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  }, []);

  const [visibleItems, setVisibleItems] = useState(itemsPerView());

  useEffect(() => {
    const handleResize = () => {
      setVisibleItems(itemsPerView());
      // Reset to first item when screen size changes
      setCurrentIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [itemsPerView]);

  const paginate = useCallback(
    (direction) => {
      setCurrentIndex((prev) => {
        const next = prev + direction;
        const maxIndex = items.length - visibleItems;
        return Math.max(0, Math.min(next, maxIndex));
      });
    },
    [items.length, visibleItems]
  );

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const dist = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - dist;
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    const scrollPosition = carouselRef.current.scrollLeft;
    const cardWidth = carouselRef.current.offsetWidth / visibleItems;
    const nearestCardIndex = Math.round(scrollPosition / cardWidth);
    setCurrentIndex(nearestCardIndex);
  };

  return (
    <div className="relative mx-auto px-4 md:px-8 max-w-[1400px]">
      <div
        ref={carouselRef}
        className="overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseUp={() => {
          handleMouseUp();
          handleDragEnd();
        }}
        onMouseLeave={() => {
          handleMouseUp();
          handleDragEnd();
        }}
        onMouseMove={handleMouseMove}
      >
        <motion.div
          className="flex"
          animate={{
            x: `-${(currentIndex * 100) / visibleItems}%`,
          }}
          transition={{
            type: "spring",
            stiffness: 150,
            damping: 20,
            mass: 0.5,
          }}
        >
          {items.map((item, index) => (
            <motion.div
              key={index}
              className={`flex-none w-full md:w-1/2 xl:w-1/3 p-4`}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              style={{
                minWidth: `${100 / visibleItems}%`,
                maxWidth: `${100 / visibleItems}%`
              }}
            >
              {renderItem(item)}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute -left-4 -right-4 top-1/2 flex justify-between items-center transform -translate-y-1/2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => paginate(-1)}
          className="p-3 rounded-full bg-white shadow-lg text-blue-600 hover:bg-blue-50 
            transition-all disabled:opacity-50 disabled:cursor-not-allowed ml-4"
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-6 h-6" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => paginate(1)}
          className="p-3 rounded-full bg-white shadow-lg text-blue-600 hover:bg-blue-50 
            transition-all disabled:opacity-50 disabled:cursor-not-allowed mr-4"
          disabled={currentIndex >= items.length - visibleItems}
        >
          <ChevronRight className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Progress Indicators */}
      <div className="flex justify-center mt-8 space-x-2">
        {Array.from({ length: Math.ceil(items.length / visibleItems) }).map(
          (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index * visibleItems)}
              className={`transition-all duration-300 rounded-full ${
                currentIndex === index * visibleItems
                  ? "w-8 h-2 bg-blue-600"
                  : "w-2 h-2 bg-blue-200 hover:bg-blue-300"
              }`}
            />
          )
        )}
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, color = "blue", onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const colorVariants = {
    blue: {
      light: "bg-blue-50/80",
      medium: "bg-blue-100",
      icon: "from-blue-500 to-indigo-500",
      hover: "group-hover:bg-blue-100/80",
      text: "text-blue-600",
      shadow: "shadow-blue-500/25",
      glow: "group-hover:shadow-blue-500/30",
      border: "border-blue-200/50",
      pattern: "stroke-blue-600/10"
    },
    purple: {
      light: "bg-purple-50/80",
      medium: "bg-purple-100",
      icon: "from-purple-500 to-pink-500",
      hover: "group-hover:bg-purple-100/80",
      text: "text-purple-600",
      shadow: "shadow-purple-500/25",
      glow: "group-hover:shadow-purple-500/30",
      border: "border-purple-200/50",
      pattern: "stroke-purple-600/10"
    },
    green: {
      light: "bg-green-50/80",
      medium: "bg-green-100",
      icon: "from-green-500 to-emerald-500",
      hover: "group-hover:bg-green-100/80",
      text: "text-green-600",
      shadow: "shadow-green-500/25",
      glow: "group-hover:shadow-green-500/30",
      border: "border-green-200/50",
      pattern: "stroke-green-600/10"
    },
    orange: {
      light: "bg-orange-50/80",
      medium: "bg-orange-100",
      icon: "from-orange-500 to-red-500",
      hover: "group-hover:bg-orange-100/80",
      text: "text-orange-600",
      shadow: "shadow-orange-500/25",
      glow: "group-hover:shadow-orange-500/30",
      border: "border-orange-200/50",
      pattern: "stroke-orange-600/10"
    },
    pink: {
      light: "bg-pink-50/80",
      medium: "bg-pink-100",
      icon: "from-pink-500 to-rose-500",
      hover: "group-hover:bg-pink-100/80",
      text: "text-pink-600",
      shadow: "shadow-pink-500/25",
      glow: "group-hover:shadow-pink-500/30",
      border: "border-pink-200/50",
      pattern: "stroke-pink-600/10"
    },
  };

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -12, scale: 1.02 }}
      onClick={onClick}
      className={`group relative h-[320px] rounded-2xl ${colorVariants[color].light} 
        p-8 transition-all duration-500 overflow-hidden cursor-pointer
        hover:shadow-2xl ${colorVariants[color].glow}
        border border-transparent hover:border-opacity-50 ${colorVariants[color].border}
        backdrop-blur-sm transform-gpu`}
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-[0.075] group-hover:opacity-[0.15] transition-opacity duration-700">
        <svg className="w-full h-full">
          <defs>
            <pattern id={`grid-${color}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" className={colorVariants[color].pattern} strokeWidth="1" />
            </pattern>
            <pattern id={`dots-${color}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" className={`fill-current ${colorVariants[color].text} opacity-30`} />
            </pattern>
            <linearGradient id={`gradient-${color}`} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" className={`${colorVariants[color].text} stop-opacity-0`} />
              <stop offset="50%" className={`${colorVariants[color].text} stop-opacity-0.1`} />
              <stop offset="100%" className={`${colorVariants[color].text} stop-opacity-0`} />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill={`url(#grid-${color})`} />
          <rect width="100%" height="100%" fill={`url(#dots-${color})`} />
          <rect width="100%" height="100%" fill={`url(#gradient-${color})`} className="opacity-40" />
        </svg>
      </div>

      {/* Animated Gradient Orb */}
      <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full 
        bg-gradient-to-br opacity-30 blur-2xl group-hover:opacity-40 transition-opacity duration-700
        animate-pulse" style={{ 
          background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
          '--tw-gradient-from': colorVariants[color].text.includes('blue') ? '#3B82F6' : 
                               colorVariants[color].text.includes('purple') ? '#9333EA' :
                               colorVariants[color].text.includes('green') ? '#10B981' :
                               colorVariants[color].text.includes('orange') ? '#F97316' : '#EC4899',
          '--tw-gradient-to': 'transparent'
        }} />

      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Icon Container */}
        <motion.div
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 12 : 0
          }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className={`h-16 w-16 rounded-xl bg-gradient-to-br ${colorVariants[color].icon} 
            p-3 text-white shadow-lg transition-transform duration-700
            ring-1 ring-white/20 backdrop-blur-sm`}
        >
          {icon}
        </motion.div>

        {/* Title */}
        <motion.h3
          animate={{ 
            color: isHovered ? colorVariants[color].text : "#111827",
            x: isHovered ? 8 : 0
          }}
          className="mt-6 text-2xl font-semibold transition-colors duration-700"
        >
          {title}
        </motion.h3>

        {/* Description */}
        <motion.p
          animate={{ 
            color: isHovered ? "#374151" : "#6B7280",
            x: isHovered ? 8 : 0
          }}
          className="mt-4 text-base leading-relaxed transition-colors duration-700"
        >
          {description}
        </motion.p>

        {/* Learn More Link */}
        <motion.div
          animate={{ 
            x: isHovered ? 12 : 0,
            opacity: isHovered ? 1 : 0.7
          }}
          className={`mt-auto flex items-center font-medium ${colorVariants[color].text} space-x-2`}
        >
          <span>Learn More</span>
          <motion.span
            animate={{ x: isHovered ? 4 : 0 }}
            className="transform transition-transform duration-500"
          >
            →
          </motion.span>
        </motion.div>

        {/* Decorative Elements */}
        <motion.div
          animate={{ opacity: isHovered ? 1 : 0 }}
          className="absolute bottom-0 right-0 w-24 h-24 -mb-12 -mr-12"
        >
          <svg viewBox="0 0 100 100" className={`w-full h-full ${colorVariants[color].text} opacity-10`}>
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </motion.div>
      </div>

      {/* Interactive Hover Glow */}
      <motion.div
        animate={{ 
          opacity: isHovered ? 0.8 : 0,
          scale: isHovered ? 1 : 0.8
        }}
        className="absolute inset-0 bg-gradient-to-br from-transparent to-white/5 
          rounded-2xl transition-opacity duration-700"
      />
    </motion.div>
  );
};

const FaqSection = () => {
  const [activeCategory, setActiveCategory] = useState("getting-started");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFaq, setActiveFaq] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // Add mouse position tracking for hover effects
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const categories = [
    {
      id: "getting-started",
      icon: <Rocket className="w-6 h-6" />,
      color: "blue",
      title: "Getting Started",
      emoji: "🚀",
      questions: [
        {
          q: "How do I join the alumni network?",
          a: "Registration is simple! Just click the 'Join Now' button, verify your alumni status with your student ID or diploma, and complete your profile. You'll get access to all features once verified.",
          icon: <UserCheck className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Registration Guide",
              href: "/guide",
              icon: <FileText className="w-4 h-4" />,
            },
            {
              text: "Verification Process",
              href: "/verify",
              icon: <ShieldCheck className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "What benefits do I get as a member?",
          a: "Members get exclusive access to job opportunities, networking events, mentorship programs, professional development resources, and the ability to connect with fellow alumni worldwide.",
          icon: <Gift className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Member Benefits",
              href: "/benefits",
              icon: <Star className="w-4 h-4" />,
            },
            {
              text: "Premium Features",
              href: "/premium",
              icon: <Crown className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "How do I complete my alumni profile?",
          a: "Visit your profile settings, fill in your educational background, professional experience, and skills. Add a profile photo and connect your social accounts to maximize your networking potential.",
          icon: <User className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Profile Guide",
              href: "/profile-guide",
              icon: <BookOpen className="w-4 h-4" />,
            },
          ],
        },
      ],
    },
    {
      id: "networking",
      icon: <Users className="w-6 h-6" />,
      color: "purple",
      title: "Networking",
      emoji: "🤝",
      questions: [
        {
          q: "How can I find alumni in my industry?",
          a: "Use our advanced search filters to find alumni by industry, company, location, or graduation year. You can also join industry-specific groups and attend networking events.",
          icon: <Search className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Search Directory",
              href: "/directory",
              icon: <List className="w-4 h-4" />,
            },
            {
              text: "Industry Groups",
              href: "/groups",
              icon: <FolderOpen className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "How do I request an introduction?",
          a: "Find the alumni you'd like to connect with and click 'Request Introduction'. Write a personalized message explaining why you'd like to connect. Our system ensures professional networking etiquette.",
          icon: <UserPlus className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Networking Tips",
              href: "/networking-tips",
              icon: <Lightbulb className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "What are Alumni Circles?",
          a: "Alumni Circles are interest-based groups where members can share insights, opportunities, and organize meetups. Join existing circles or create your own to build meaningful connections.",
          icon: <Users className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Browse Circles",
              href: "/circles",
              icon: <Circle className="w-4 h-4" />,
            },
          ],
        },
      ],
    },
    {
      id: "careers",
      icon: <Briefcase className="w-6 h-6" />,
      color: "green",
      title: "Careers & Jobs",
      emoji: "💼",
      questions: [
        {
          q: "How do I post job opportunities?",
          a: "Verified alumni can post jobs through our dedicated portal. Click on 'Post a Job' in the Jobs section and follow the simple submission process.",
          icon: <PlusCircle className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Post a Job",
              href: "/post-job",
              icon: <Plus className="w-4 h-4" />,
            },
            {
              text: "Job Guidelines",
              href: "/job-guidelines",
              icon: <ClipboardList className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "How does the referral system work?",
          a: "Our referral system connects job seekers with alumni at target companies. Request a referral through the job posting, and the alumnus can choose to endorse your application.",
          icon: <Share2 className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Referral Guide",
              href: "/referrals",
              icon: <HelpCircle className="w-4 h-4" />,
            },
          ],
        },
      ],
    },
    {
      id: "events",
      icon: <Calendar className="w-6 h-6" />,
      color: "orange",
      title: "Events",
      emoji: "📅",
      questions: [
        {
          q: "How do I register for events?",
          a: "Browse the Events section, select an event you're interested in, and click 'Register Now'. You'll receive confirmation and event updates via email.",
          icon: <CalendarPlus className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Upcoming Events",
              href: "/events",
              icon: <Calendar className="w-4 h-4" />,
            },
            {
              text: "Event Calendar",
              href: "/calendar",
              icon: <CalendarDays className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "Can I host an alumni event?",
          a: "Yes! Verified alumni can propose events through the 'Host an Event' form. Our team will review and help you organize it.",
          icon: <Users className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Host Guidelines",
              href: "/host-guide",
              icon: <FileText className="w-4 h-4" />,
            },
          ],
        },
      ],
    },
    {
      id: "mentorship",
      icon: <HeartHandshake className="w-6 h-6" />,
      color: "pink",
      title: "Mentorship",
      emoji: "🌟",
      questions: [
        {
          q: "How does the mentorship program work?",
          a: "You can sign up as either a mentor or mentee. Our matching algorithm pairs you based on your industry, interests, and goals. Mentorship relationships typically last 6 months.",
          icon: <Users className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Mentorship Guide",
              href: "/mentorship-guide",
              icon: <BookOpen className="w-4 h-4" />,
            },
            {
              text: "Success Stories",
              href: "/success-stories",
              icon: <Star className="w-4 h-4" />,
            },
          ],
        },
        {
          q: "What's expected of mentors?",
          a: "Mentors typically commit to monthly meetings with their mentees, provide career guidance, and share their professional experiences. We provide resources and best practices to help mentors succeed.",
          icon: <Target className="w-5 h-5" />,
          relatedLinks: [
            {
              text: "Mentor Resources",
              href: "/mentor-resources",
              icon: <Book className="w-4 h-4" />,
            },
          ],
        },
      ],
    },
  ];

  const handleMouseMove = (e) => {
    setMousePosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Enhanced search with highlighting
  const highlightSearch = (text) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <mark
              key={i}
              className="bg-yellow-100 text-gray-900 rounded-sm px-1"
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-70" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, 
            rgba(59, 130, 246, 0.1) 0%, 
            rgba(147, 51, 234, 0.1) 100%)`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4" onMouseMove={handleMouseMove}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span
            className="inline-block mb-4 px-4 py-2 rounded-full bg-blue-100 
            text-blue-600 text-sm font-medium"
          >
            Frequently Asked Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How can we help you?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about our alumni network. Can't find an
            answer? Our support team is just a click away.
          </p>
        </motion.div>

        {/* Enhanced Search Bar */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-2xl mx-auto mb-12 relative"
        >
          <motion.div
            animate={{
              scale: searchFocused ? 1.02 : 1,
              boxShadow: searchFocused
                ? "0 8px 30px rgba(0,0,0,0.12)"
                : "0 4px 6px rgba(0,0,0,0.05)",
            }}
            className="relative rounded-2xl bg-white transition-all duration-300"
          >
            <Search
              className="absolute left-6 top-1/2 transform -translate-y-1/2 
              text-gray-400 w-5 h-5"
            />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className="w-full pl-16 pr-6 py-4 rounded-2xl border-2 border-transparent
                focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all
                text-lg placeholder-gray-400"
            />
            {searchTerm && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => setSearchTerm("")}
                className="absolute right-6 top-1/2 transform -translate-y-1/2
                  text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </motion.button>
            )}
          </motion.div>

          {/* Search Suggestions */}
          {searchTerm && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl
                shadow-lg border border-gray-100 p-2 z-10"
            >
              <div className="text-sm text-gray-500 px-3 py-2">
                Popular searches
              </div>
              {["membership", "jobs", "events", "mentorship"].map(
                (term, index) => (
                  <motion.button
                    key={term}
                    whileHover={{ x: 4 }}
                    onClick={() => setSearchTerm(term)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50
                    text-gray-700 flex items-center space-x-2"
                  >
                    <Search className="w-4 h-4 text-gray-400" />
                    <span>{term}</span>
                  </motion.button>
                )
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Category Navigation */}
          <div className="md:col-span-4 lg:col-span-3 space-y-3">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center space-x-4 p-4 rounded-xl
                  transition-all duration-300 ${
                    activeCategory === category.id
                      ? `bg-${category.color}-500 text-white shadow-lg`
                      : "bg-white hover:bg-gray-50"
                  }`}
              >
                <div
                  className={`p-2 rounded-xl ${
                    activeCategory === category.id
                      ? "bg-white/20"
                      : `bg-${category.color}-100 text-${category.color}-600`
                  }`}
                >
                  {category.icon}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-semibold">{category.title}</div>
                  <div
                    className={`text-sm ${
                      activeCategory === category.id
                        ? "text-white/80"
                        : "text-gray-500"
                    }`}
                  >
                    {category.questions.length} articles
                  </div>
                </div>
                <ChevronRight
                  className={`w-5 h-5 transition-transform ${
                    activeCategory === category.id ? "rotate-90" : ""
                  }`}
                />
              </motion.button>
            ))}
          </div>

          {/* FAQ Content Area */}
          <div className="md:col-span-8 lg:col-span-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-lg"
              >
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`p-3 rounded-xl bg-${
                        categories.find((c) => c.id === activeCategory)?.color
                      }-100 text-${
                        categories.find((c) => c.id === activeCategory)?.color
                      }-600`}
                    >
                      {categories.find((c) => c.id === activeCategory)?.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {categories.find((c) => c.id === activeCategory)?.title}
                      </h3>
                      <p className="text-gray-500">
                        Find answers to common questions about{" "}
                        {categories
                          .find((c) => c.id === activeCategory)
                          ?.title.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {categories
                    .find((c) => c.id === activeCategory)
                    ?.questions.filter(
                      (q) =>
                        !searchTerm ||
                        q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        q.a.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((faq, index) => (
                      <motion.div
                        key={index}
                        initial={false}
                        animate={{
                          backgroundColor:
                            activeFaq === `${activeCategory}-${index}`
                              ? "rgb(243, 244, 246)"
                              : "white",
                        }}
                        className="group"
                      >
                        <motion.button
                          onClick={() =>
                            setActiveFaq(
                              activeFaq === `${activeCategory}-${index}`
                                ? null
                                : `${activeCategory}-${index}`
                            )
                          }
                          className="w-full px-6 py-4 flex items-start justify-between
                            text-left transition-colors hover:bg-gray-50"
                        >
                          <div className="flex items-start space-x-4 flex-1">
                            <div
                              className={`p-2 rounded-lg bg-${
                                categories.find((c) => c.id === activeCategory)
                                  ?.color
                              }-100 text-${
                                categories.find((c) => c.id === activeCategory)
                                  ?.color
                              }-600 mt-1`}
                            >
                              {faq.icon}
                            </div>
                            <span className="font-medium text-gray-900">
                              {highlightSearch(faq.q)}
                            </span>
                          </div>
                          <motion.div
                            animate={{
                              rotate:
                                activeFaq === `${activeCategory}-${index}`
                                  ? 180
                                  : 0,
                              color:
                                activeFaq === `${activeCategory}-${index}`
                                  ? categories.find(
                                      (c) => c.id === activeCategory
                                    )?.color
                                  : "gray",
                            }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 flex-shrink-0 text-gray-400"
                          >
                            <ChevronDown className="w-5 h-5" />
                          </motion.div>
                        </motion.button>

                        <AnimatePresence>
                          {activeFaq === `${activeCategory}-${index}` && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="px-6 pb-4"
                            >
                              <div className="prose prose-blue max-w-none">
                                <p className="text-gray-600">
                                  {highlightSearch(faq.a)}
                                </p>
                              </div>

                              {/* Related Links */}
                              {faq.relatedLinks && (
                                <div className="mt-4 flex flex-wrap gap-3">
                                  {faq.relatedLinks.map((link, linkIndex) => (
                                    <motion.a
                                      key={linkIndex}
                                      href={link.href}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className={`inline-flex items-center space-x-2 px-4 py-2 
                                        rounded-lg text-sm font-medium bg-${
                                          categories.find(
                                            (c) => c.id === activeCategory
                                          )?.color
                                        }-50 text-${
                                        categories.find(
                                          (c) => c.id === activeCategory
                                        )?.color
                                      }-600 hover:bg-${
                                        categories.find(
                                          (c) => c.id === activeCategory
                                        )?.color
                                      }-100 transition-colors`}
                                    >
                                      {link.icon}
                                      <span>{link.text}</span>
                                    </motion.a>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

      
      </div>
    </section>
  );
};

const AlumniCard = ({ alumnus }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const getPlaceholderAvatar = () => {
    const colors = ["4F46E5", "7C3AED", "E11D48", "EA580C", "059669"];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    return `https://ui-avatars.com/api/?name=${alumnus.firstName}+${alumnus.lastName}&background=${randomColor}&color=fff&size=200`;
  };

  const hasCurrentRole = alumnus.professionalInfo?.experiences?.some(
    (e) => e.isCurrent
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-sm 
        hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-[0_4px_12px_rgb(0,0,0,0.08)] 
        border border-gray-100/50 transition-all duration-300 h-[340px]" // Fixed height
    >
      {/* Background Pattern - Unchanged */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.04),transparent_50%)]" />
        <svg className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
          <defs>
            <pattern
              id="smallGrid"
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 10 0 L 0 0 0 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.3"
              />
            </pattern>
            <pattern
              id="dots"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="2"
                cy="2"
                r="1"
                fill="currentColor"
                fillOpacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#smallGrid)" />
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
      </div>

      <div className="relative flex flex-col h-full p-6">
        {/* Header Section - Fixed Height */}
        <div className="flex items-start space-x-4 h-16">
          {/* Avatar Section */}
          <div className="relative group flex-shrink-0">
            <div
              className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-purple-500 
              rounded-2xl opacity-0 group-hover:opacity-20 blur transition-all duration-300"
            />
            <div
              className="relative h-16 w-16 rounded-2xl overflow-hidden bg-gradient-to-br 
              from-blue-50 to-indigo-50 ring-1 ring-white/50 shadow-inner"
            >
              <img
                src={alumnus.personalInfo?.avatar || getPlaceholderAvatar()}
                alt={`${alumnus.firstName} ${alumnus.lastName}`}
                className="h-full w-full object-cover"
              />
            </div>
            {alumnus.isVerified && (
              <div className="absolute -bottom-1 -right-1">
                <span className="relative flex h-5 w-5">
                  <span
                    className="animate-ping absolute inline-flex h-full w-full rounded-full 
                    bg-green-400 opacity-20"
                  ></span>
                  <span
                    className="relative inline-flex rounded-full h-5 w-5 bg-gradient-to-r 
                    from-green-500 to-emerald-500 text-white justify-center items-center"
                  >
                    <CheckCircle className="h-3 w-3" />
                  </span>
                </span>
              </div>
            )}
          </div>

          {/* Name & Title Section */}
          <div className="flex-1 min-w-0">
            <h3
              className="text-lg font-semibold bg-clip-text text-transparent 
              bg-gradient-to-r from-gray-900 to-gray-700 truncate"
            >
              {alumnus.firstName} {alumnus.lastName}
            </h3>
            <div className="mt-1 flex items-center text-sm text-gray-600 space-x-2">
              <GraduationCap className="h-4 w-4 flex-shrink-0 text-indigo-400" />
              <span className="truncate">
                {alumnus.academicInfo?.department || alumnus.department}
              </span>
              <span className="flex-shrink-0">• {alumnus.graduationYear}</span>
            </div>
          </div>
        </div>

        {/* Professional Info - Fixed Height */}
        {hasCurrentRole && (
          <div className="mt-6 grid grid-cols-2 gap-3 h-20">
            <div
              className="relative p-3 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/30 
              border border-slate-100 group-hover:border-blue-100/50 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Briefcase className="h-4 w-4 flex-shrink-0 text-blue-500" />
                <span className="text-sm font-medium text-gray-800 truncate">
                  {
                    alumnus.professionalInfo.experiences.find(
                      (e) => e.isCurrent
                    ).title
                  }
                </span>
              </div>
              <div className="mt-1 flex items-center space-x-2">
                <Building className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-sm text-gray-600 truncate">
                  {
                    alumnus.professionalInfo.experiences.find(
                      (e) => e.isCurrent
                    ).company
                  }
                </span>
              </div>
            </div>

            <div
              className="relative p-3 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/30 
              border border-slate-100 group-hover:border-blue-100/50 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 flex-shrink-0 text-purple-500" />
                <span className="text-sm font-medium text-gray-800 truncate">
                  {
                    alumnus.professionalInfo.experiences.find(
                      (e) => e.isCurrent
                    ).location
                  }
                </span>
              </div>
              <div className="mt-1 flex items-center space-x-2">
                <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-sm text-gray-600 truncate">
                  {new Date().getFullYear() -
                    new Date(
                      alumnus.professionalInfo.experiences.find(
                        (e) => e.isCurrent
                      ).startDate
                    ).getFullYear()}{" "}
                  years
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Skills Section - Fixed Height */}
        {alumnus.professionalInfo?.skills?.length > 0 && (
          <div className="mt-6 h-8 overflow-hidden">
            <div className="flex flex-wrap gap-2">
              {alumnus.professionalInfo.skills
                .slice(0, 3)
                .map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r 
                    from-white to-blue-50 text-gray-700 border border-blue-100/50 
                    group-hover:border-blue-200/50 transition-colors truncate"
                  >
                    {skill}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Footer - Fixed Position */}
        <div className="mt-auto pt-4 border-t border-gray-100/80">
          <div className="flex items-center justify-between">
            {alumnus.joinedOn && (
              <div className="flex items-center space-x-2 text-gray-400">
                <Calendar className="w-4 h-4" />
                <span className="text-sm truncate">
                  Joined{" "}
                  {formatDistanceToNow(new Date(alumnus.joinedOn), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/alumni/${alumnus._id}`)}
              className="inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r 
                from-blue-500 to-indigo-600 text-white text-sm font-medium shadow-sm 
                hover:shadow-md transition-all"
            >
              View Profile
              <ChevronRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const JobCard = ({ job, onViewDetails }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const getCompanyLogo = (companyName) => {
    return COMPANY_LOGOS[companyName] || `/api/placeholder/100/100`;
  };

  const formatSalary = (min, max) => {
    const formatNumber = (num) => {
      if (num >= 100000) {
        return `${(num / 100000).toFixed(1)}L`;
      }
      return `${(num / 1000).toFixed(0)}K`;
    };
    return `${formatNumber(min)} - ${formatNumber(max)}`;
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-sm 
        hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-[0_4px_12px_rgb(0,0,0,0.08)] 
        border border-gray-100/50 transition-all duration-300 h-[370px]" // Fixed height
    >
      {/* Background Pattern - Unchanged */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/50 to-blue-50/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.04),transparent_50%)]" />
        <svg className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
          <defs>
            <pattern
              id="tiny-grid"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.3"
              />
            </pattern>
            <pattern
              id="job-dots"
              width="16"
              height="16"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="2"
                cy="2"
                r="1"
                fill="currentColor"
                fillOpacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tiny-grid)" />
          <rect width="100%" height="100%" fill="url(#job-dots)" />
        </svg>
      </div>

      <div className="relative flex flex-col h-full p-6">
        {/* Header Section - Fixed Height */}
        <div className="flex items-start space-x-4 h-16">
          {/* Company Logo */}
          <div className="relative group flex-shrink-0">
            <div
              className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-purple-500 
              rounded-2xl opacity-0 group-hover:opacity-20 blur transition-all duration-300"
            />
            <div
              className="relative h-16 w-16 rounded-2xl overflow-hidden bg-gradient-to-br 
              from-slate-50 to-white ring-1 ring-gray-100/50 shadow-sm p-2"
            >
              <img
                src={getCompanyLogo(job.companyName)}
                alt={job.companyName}
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Title and Company - Limited Height */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <h3
                  className="text-xl font-semibold bg-clip-text text-transparent 
                  bg-gradient-to-r from-gray-900 to-gray-700 group-hover:from-blue-600 
                  group-hover:to-indigo-600 transition-all duration-300 truncate"
                >
                  {job.title}
                </h3>
                <p className="text-gray-600 mt-1 flex items-center space-x-2">
                  <Building className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{job.companyName}</span>
                </p>
              </div>
              {job.isRemote && (
                <span
                  className="flex-shrink-0 px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r 
                  from-emerald-50 to-green-50 text-emerald-600 border border-emerald-100/50"
                >
                  Remote
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Job Details Grid - Fixed Height */}
        <div className="mt-6 grid grid-cols-2 gap-3 h-16">
          <div
            className="relative p-3 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/30 
            border border-slate-100 group-hover:border-blue-100/50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-rose-500" />
              <span className="text-sm font-medium text-gray-800 truncate">
                {job.location}
              </span>
            </div>
            <div className="mt-1 flex items-center space-x-2">
              <Briefcase className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 truncate">{job.type}</span>
            </div>
          </div>

          <div
            className="relative p-3 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/30 
            border border-slate-100 group-hover:border-blue-100/50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-800 truncate">
                {job.experience}
              </span>
            </div>
            <div className="mt-1 flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-emerald-600 truncate">
                ₹
                {formatSalary(
                  job.jobDetails.salary.min,
                  job.jobDetails.salary.max
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Skills Section - Fixed Height */}
        <div className="mt-6 h-10 overflow-hidden">
          {job.skills?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {job.skills.slice(0, 4).map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r 
                    from-white to-blue-50 text-gray-700 border border-blue-100/50 
                    group-hover:border-blue-200/50 transition-colors truncate"
                >
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer Section - Fixed Position */}
        <div className="mt-auto pt-4 border-t border-gray-100/80">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Posted {job.postedDate}</span>
              <span className="block w-1 h-1 rounded-full bg-gray-300" />
              <span className="text-sm text-emerald-600 font-medium">
                Active
              </span>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onViewDetails(job.id)}
            className="w-full inline-flex items-center justify-center px-5 py-3 rounded-xl 
              bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium 
              shadow-sm hover:shadow-md transition-all"
          >
            View Details
            <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const CampaignCard = ({ campaign, onViewDetails }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Fixed progress calculation
  const calculateProgress = () => {
    const raised = campaign?.financials?.raisedAmount || 0;
    const target = campaign?.financials?.targetAmount;
    if (!target || target === 0) return 0;
    return (raised / target) * 100;
  };

  const progress = calculateProgress();

  const getDescription = () => {
    if (!campaign.description) return "";
    return campaign.description.short || campaign.description.detailed || "";
  };

  const getDaysRemaining = () => {
    if (!campaign.timeline?.endDate) return null;
    const diff = new Date(campaign.timeline.endDate) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const daysRemaining = getDaysRemaining();

  // Get primary image or first available image
  const getCoverImage = () => {
    if (!campaign.media?.images || campaign.media.images.length === 0) {
      return "/api/placeholder/400/320";
    }
    const primaryImage = campaign.media.images.find(img => img.isPrimary)?.url;
    return primaryImage || campaign.media.images[0]?.url || "/api/placeholder/400/320";
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-sm 
        hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-[0_4px_12px_rgb(0,0,0,0.08)] 
        border border-gray-100/50 transition-all duration-300 h-[520px]"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-100 opacity-70">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/50 to-indigo-50/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(79,70,229,0.08),transparent_50%)]" />
        <svg className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-700">
          <defs>
            <pattern
              id="campaign-grid"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.3"
              />
            </pattern>
            <pattern
              id="campaign-dots"
              width="16"
              height="16"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="2"
                cy="2"
                r="1"
                fill="currentColor"
                fillOpacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#campaign-grid)" />
          <rect width="100%" height="100%" fill="url(#campaign-dots)" />
        </svg>
      </div>

      {/* Cover Image with Overlay */}
      <div className="relative h-40 w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 z-10" />
        <motion.img
          src={getCoverImage()}
          alt={campaign.title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-700"
        />
        {/* Category Badge */}
        <div className="absolute top-4 left-4 z-20">
          <span
            className="px-3 py-1.5 rounded-full text-sm font-medium 
            bg-gradient-to-r from-blue-500/90 to-indigo-500/90 
            text-white backdrop-blur-sm shadow-lg border border-white/10"
          >
            {campaign.category}
          </span>
        </div>
        {campaign.status === "published" && (
          <div className="absolute top-4 right-4 z-20">
            <span
              className="px-3 py-1.5 rounded-full text-sm font-medium 
              bg-gradient-to-r from-emerald-500/90 to-green-500/90 
              text-white backdrop-blur-sm shadow-lg border border-white/10"
            >
              Active
            </span>
          </div>
        )}
      </div>

      <div className="relative p-4 flex flex-col h-[calc(100%-10rem)] justify-between">
        {/* Title & Description */}
        <div >
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1 
            group-hover:text-blue-600 transition-colors duration-300">
            {campaign.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2 mb-4">
            {campaign.tagline || getDescription()}
          </p>
        </div>

       {/* Progress Section */}
       <div className="space-y-3">
          <div className="relative h-6">
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full
                  relative overflow-hidden shadow-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent" />
              </motion.div>
            </div>
            <span className="absolute -top-1 right-0 text-sm font-medium text-blue-600">
              {progress.toFixed(1)}%
            </span>
          </div>
          {/* Amount Details */}
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-600">
              <span className="font-semibold text-blue-600">
                ₹{(campaign.financials?.raisedAmount || 0).toLocaleString()}
              </span>{" "}
              raised of ₹{(campaign.financials?.targetAmount || 0).toLocaleString()}
            </div>
            {daysRemaining !== null && (
              <div className="flex items-center text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                <span>{daysRemaining} days left</span>
              </div>
            )}
          </div>
        </div>

        {/* Organizer & Donors Info */}
        <div className="flex items-center justify-between mt-4 mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <img
                src={campaign.organizer?.organizedBy?.personalInfo?.avatar || "/api/placeholder/32/32"}
                alt={`${campaign.organizer?.organizedBy?.firstName || 'Organizer'}'s avatar`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {campaign.organizer?.organizedBy?.firstName} {campaign.organizer?.organizedBy?.lastName}
              </p>
              <p className="text-gray-500 text-xs">Organizer</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-gray-600">
            <Users className="w-4 h-4" />
            <span className="text-sm">{campaign.donations?.length || 0} donors</span>
          </div>
        </div>

        {/* Action Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onViewDetails(campaign._id)}
          className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 
            hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl font-medium 
            shadow-sm hover:shadow-md transition-all duration-300
            flex items-center justify-center space-x-2 group/btn"
        >
          <Heart className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-300" />
          <span className="group-hover/btn:translate-x-0.5 transition-transform duration-300">
            Support Campaign
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
};


const EventCard = ({ event, onViewDetails }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const getFormattedDate = (dateString) => {
    try {
      if (!dateString) return "Date not set";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";
      return formatDate(date, "PPP");
    } catch (error) {
      return "Date unavailable";
    }
  };

  const getDescription = (desc) => {
    if (!desc) return "";
    if (typeof desc === "string") return desc;
    if (desc.short) return desc.short;
    if (desc.detailed) return desc.detailed;
    return JSON.stringify(desc);
  };

  const getEventStatus = () => {
    const now = new Date();
    const startDate = new Date(event.schedule?.startDate);
    const endDate = new Date(event.schedule?.endDate);

    if (event.capacity?.remaining === 0)
      return { text: "Sold Out", color: "from-red-500 to-rose-600" };
    if (now > endDate)
      return { text: "Ended", color: "from-gray-500 to-slate-600" };
    if (now >= startDate && now <= endDate)
      return { text: "Ongoing", color: "from-emerald-500 to-green-600" };
    return { text: "Upcoming", color: "from-blue-500 to-indigo-600" };
  };

  const status = getEventStatus();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-sm 
        hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-[0_4px_12px_rgb(0,0,0,0.08)] 
        border border-gray-100/50 transition-all duration-300 h-[500px]"
    >
      {/* Enhanced Background Pattern with Subtle Animation */}
      <div className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-100 opacity-70">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/50 to-indigo-50/10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.06),transparent_50%)]" />
        <svg className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-700">
          <defs>
            <pattern
              id="event-grid"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.3"
              />
            </pattern>
            <pattern
              id="event-dots"
              width="16"
              height="16"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="2"
                cy="2"
                r="1"
                fill="currentColor"
                fillOpacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#event-grid)" />
          <rect width="100%" height="100%" fill="url(#event-dots)" />
        </svg>
      </div>

      {/* Enhanced Image Section with Overlay */}
      <div className="relative h-48 w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 z-10 
          group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500"
        />
        <motion.img
          src={event.images?.[0] || "/api/placeholder/400/320"}
          alt={event.title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-700"
        />
        {/* Enhanced Event Type Badge */}
        <div className="absolute top-4 left-4 z-20">
          <span
            className={`px-3 py-1.5 rounded-full text-sm font-medium 
            ${
              event.type === "reunion"
                ? "bg-gradient-to-r from-purple-500/90 to-fuchsia-500/90"
                : "bg-gradient-to-r from-blue-500/90 to-indigo-500/90"
            } 
            text-white backdrop-blur-sm shadow-lg border border-white/10`}
          >
            {event.type}
          </span>
        </div>
        {/* Enhanced Status Badge */}
        <div className="absolute top-4 right-4 z-20">
          <span
            className={`px-3 py-1.5 rounded-full text-sm font-medium 
            bg-gradient-to-r ${status.color} 
            text-white backdrop-blur-sm shadow-lg border border-white/10`}
          >
            {status.text}
          </span>
        </div>
      </div>

      <div className="relative p-6 flex flex-col h-[280px]">
        {/* Title & Description Section - Fixed Height */}
        <div className="mb-4">
          <h3
            className="text-xl font-semibold bg-clip-text text-transparent 
            bg-gradient-to-r from-gray-900 to-gray-700 mb-2 line-clamp-1 group-hover:from-indigo-600 
            group-hover:to-purple-600 transition-all duration-300"
          >
            {event.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-1 h-5">
            {getDescription(event.description)}
          </p>
        </div>

        {/* Event Details Grid - Fixed Height */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div
            className="p-3 rounded-xl bg-gradient-to-br from-slate-50 to-indigo-50/30 
            border border-slate-100 group-hover:border-indigo-100/50 transition-all duration-300
            hover:shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-indigo-500" />
              <span className="text-sm font-medium text-gray-800 truncate">
                {event.venue?.name || "Venue TBA"}
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-600 truncate">
              {event.venue?.city || "Location TBA"}
            </div>
          </div>

          <div
            className="p-3 rounded-xl bg-gradient-to-br from-slate-50 to-emerald-50/30 
            border border-slate-100 group-hover:border-emerald-100/50 transition-all duration-300
            hover:shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium text-gray-800 line-clamp-1">
                {event.capacity?.remaining || 0} spots left
              </span>
            </div>
            <div className="mt-1 text-sm text-emerald-600 font-medium">
              Register soon!
            </div>
          </div>
        </div>

        {/* Date & Time Section - Fixed Height */}
        {event.schedule?.startDate && (
          <div className="mb-4">
            <div
              className="p-3 rounded-xl bg-gradient-to-br from-amber-50/80 to-orange-50/30 
              border border-amber-100/50 group-hover:border-amber-200/50 transition-all duration-300
              hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium text-gray-800">
                    {getFormattedDate(event.schedule.startDate)}
                  </span>
                </div>
                {event.schedule.endDate && (
                  <div className="text-sm text-gray-500 font-medium">
                    {formatDate(new Date(event.schedule.endDate), "p")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Button - Fixed Position */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onViewDetails(event._id)}
          disabled={event.capacity?.remaining === 0}
          className={`mt-auto w-full px-4 py-3 bg-gradient-to-r 
            ${
              event.capacity?.remaining === 0
                ? "from-gray-400 to-gray-500 cursor-not-allowed"
                : "from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            } 
            text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-300
            flex items-center justify-center space-x-2 group/btn`}
        >
          <Calendar className="w-4 h-4 group-hover/btn:rotate-12 transition-transform duration-300" />
          <span className="group-hover/btn:translate-x-0.5 transition-transform duration-300">
            {event.capacity?.remaining === 0 ? "Sold Out" : "Register Now"}
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
};

const NewsletterCard = ({ newsletter, onViewDetails }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "";
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-sm 
        hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] shadow-[0_4px_12px_rgb(0,0,0,0.08)] 
        border border-gray-100/50 transition-all duration-300 h-[430px]"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-100 opacity-70">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/50 to-blue-50/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.04),transparent_50%)]" />
        <svg className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity">
          <defs>
            <pattern
              id="newsletter-grid"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.3"
              />
            </pattern>
            <pattern
              id="newsletter-dots"
              width="16"
              height="16"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="2"
                cy="2"
                r="1"
                fill="currentColor"
                fillOpacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#newsletter-grid)" />
          <rect width="100%" height="100%" fill="url(#newsletter-dots)" />
        </svg>
      </div>

      {/* Cover Image */}
      <div className="relative h-48 w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 z-10" />
        <motion.img
          src={newsletter.coverImage || "/api/placeholder/400/320"}
          alt={newsletter.title}
          className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-700"
        />
        {/* Category Badge */}
        <div className="absolute top-4 left-4 z-20">
          <span
            className="px-3 py-1.5 rounded-full text-sm font-medium 
            bg-gradient-to-r from-blue-500/90 to-indigo-500/90 
            text-white backdrop-blur-sm shadow-lg border border-white/10"
          >
            {newsletter.category}
          </span>
        </div>
        {newsletter.featured && (
          <div className="absolute top-4 right-4 z-20">
            <span
              className="px-3 py-1.5 rounded-full text-sm font-medium 
              bg-gradient-to-r from-amber-500/90 to-orange-500/90 
              text-white backdrop-blur-sm shadow-lg border border-white/10"
            >
              Featured
            </span>
          </div>
        )}
      </div>

      <div className="relative p-6 flex flex-col h-[calc(100%-12rem)]">
        {/* Title & Subtitle */}
        <div className="mb-4">
          <h3
            className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2 
            group-hover:text-blue-600 transition-colors duration-300"
          >
            {newsletter.title}
          </h3>
          {newsletter.subtitle && (
            <p className="text-gray-600 text-sm line-clamp-2">
              {newsletter.subtitle}
            </p>
          )}
        </div>

        {/* Author & Date */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{newsletter.author?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(newsletter.createdAt)}</span>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-auto flex justify-between items-center">
          <div className="flex items-center gap-2 text-gray-500">
            <Eye className="w-4 h-4" />
            <span className="text-sm">{newsletter.viewCount} views</span>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onViewDetails(newsletter._id)}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 
              text-white rounded-xl font-medium shadow-sm hover:shadow-md 
              transition-all flex items-center gap-2"
          >
            Read More
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};
const SectionLoadingAnimation = () => (
  <div className="flex justify-center items-center h-64">
    <div className="relative">
      <div className="w-12 h-12 rounded-full border-4 border-blue-200 animate-pulse" />
      <div className="w-12 h-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin absolute top-0" />
    </div>
  </div>
);
const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const [currentAlumni, setCurrentAlumni] = useState(null);
const [currentAlumniLoading, setCurrentAlumniLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState({
    alumni: true,
    jobs: true,
    campaigns: true,
    events: true,
    newsletters: true,
    stats: true,
  });
  const [data, setData] = useState({
    alumni: [],
    jobs: [],
    campaigns: [],
    events: [],
    news: [],
    features: [
      {
        icon: <Briefcase className="w-6 h-6" />,
        title: "Job Opportunities",
        description: "Access exclusive job and career opportunities  globally.",
        color: "blue",
        path: "/jobs",
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: "Alumni Network",
        description:
          "Connect with a diverse community of successful graduates across industries.",
        color: "purple",
        path: "/network",
      },
      {
        icon: <GraduationCap className="w-6 h-6" />,
        title: "Mentorship",
        description:
          "Get guidance from experienced professionals in your field of interest.",
        color: "green",
        path: "/mentorship",
      },
      {
        icon: <Calendar className="w-6 h-6" />,
        title: "Events",
        description:
          "Participate in exclusive networking events, webinars, and workshops.",
        color: "orange",
        path: "/events",
      },
      {
        icon: <Trophy className="w-6 h-6" />,
        title: "Success Stories",
        description: "Get inspired by the achievements of fellow alumni.",
        color: "pink",
        path: "/stories",
      },
      {
        icon: <HeartHandshake className="w-6 h-6" />,
        title: "Give Back",
        description:
          "Contribute to the community through various initiatives and programs.",
        color: "blue",
        path: "/contribute",
      },
    ],
  });

  const [stats, setStats] = useState({
    totalAlumni: 0,
    verifiedAlumni: 0,
    totalCompanies: 0,
    activeJobs: 0,
    totalRaised: 0,
    upcomingEvents: 0,
    countries: 0,
    cities: 0,
    totalDonors: 0,
    averageSalary: 0,
    referralJobs: 0,
    successfulPlacements: 0,
    mentorshipHours: 0,
    ongoingCampaigns: 0,
  });
  const calculateEnhancedStats = (fetchedData) => {
    const { alumni, jobs, campaigns, events } = fetchedData;

    try {
      // Calculate unique companies, countries, and cities
      const uniqueCompanies = new Set(
        alumni
          .map(
            (a) =>
              a.professionalInfo?.experiences?.find((e) => e.isCurrent)?.company
          )
          .filter(Boolean)
      );

      const uniqueCountries = new Set(
        alumni
          .map((a) =>
            a.professionalInfo?.experiences
              ?.find((e) => e.isCurrent)
              ?.location?.split(",")
              .pop()
              ?.trim()
          )
          .filter(Boolean)
      );

      const uniqueCities = new Set(
        alumni
          .map((a) =>
            a.professionalInfo?.experiences
              ?.find((e) => e.isCurrent)
              ?.location?.split(",")[0]
              ?.trim()
          )
          .filter(Boolean)
      );

      // Calculate average salary safely
      const salaries = jobs
        .map((j) =>
          j.jobDetails?.salary?.min && j.jobDetails?.salary?.max
            ? (j.jobDetails.salary.min + j.jobDetails.salary.max) / 2
            : null
        )
        .filter(Boolean);

      const avgSalary = salaries.length
        ? salaries.reduce((a, b) => a + b, 0) / salaries.length
        : 0;

      // Update stats with calculated values
      setStats({
        totalAlumni: alumni?.length || 0,
        verifiedAlumni: alumni?.filter((a) => a.isVerified)?.length || 0,
        totalCompanies: uniqueCompanies.size,
        activeJobs:
          jobs?.filter(
            (j) =>
              j.applicationDetails?.deadline &&
              new Date(j.applicationDetails.deadline) > new Date()
          )?.length || 0,
        totalRaised:
          campaigns?.reduce((sum, c) => sum + (c.raisedAmount || 0), 0) || 0,
        upcomingEvents:
          events?.filter((e) => e.date && new Date(e.date) > new Date())
            ?.length || 0,
        countries: uniqueCountries.size,
        cities: uniqueCities.size,
        totalDonors:
          campaigns?.reduce((sum, c) => sum + (c.donors?.length || 0), 0) || 0,
        averageSalary: avgSalary,
        referralJobs: jobs?.filter((j) => j.referralEnabled)?.length || 0,
        successfulPlacements: Math.floor((alumni?.length || 0) * 0.85),
        mentorshipHours: (alumni?.length || 0) * 10,
        ongoingCampaigns:
          campaigns?.filter(
            (c) =>
              c.timeline?.endDate && new Date(c.timeline.endDate) > new Date()
          )?.length || 0,
      });
    } catch (error) {
      console.error("Error calculating stats:", error);
      // Set default stats in case of error
      setStats({
        totalAlumni: 0,
        verifiedAlumni: 0,
        totalCompanies: 0,
        activeJobs: 0,
        totalRaised: 0,
        upcomingEvents: 0,
        countries: 0,
        cities: 0,
        totalDonors: 0,
        averageSalary: 0,
        referralJobs: 0,
        successfulPlacements: 0,
        mentorshipHours: 0,
        ongoingCampaigns: 0,
      });
    }
  };
  useEffect(() => {
    const fetchDataWithRetry = async (url, options, retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          const response = await fetch(url, options);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return await response.json();
        } catch (error) {
          if (i === retries - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };
    const fetchCurrentAlumni = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
    
        if (!response.ok) {
          throw new Error('Failed to fetch current alumni');
        }
    
        const data = await response.json();
        setCurrentAlumni(data);
      } catch (error) {
        console.error("Error fetching current alumni:", error);
      } finally {
        setCurrentAlumniLoading(false);
      }
    };
    const fetchAlumni = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const alumni = await fetchDataWithRetry(`${process.env.REACT_APP_BASE_URL}/api/alumni/directory`, { headers });
        setData(prev => ({ ...prev, alumni }));
        setLoadingStates(prev => ({ ...prev, alumni: false }));
        return alumni;
      } catch (error) {
        console.error("Error fetching alumni:", error);
        setLoadingStates(prev => ({ ...prev, alumni: false }));
        return [];
      }
    };

    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const jobs = await fetchDataWithRetry(`${process.env.REACT_APP_BASE_URL}/api/jobs`, { headers });
        setData(prev => ({ ...prev, jobs }));
        setLoadingStates(prev => ({ ...prev, jobs: false }));
        return jobs;
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setLoadingStates(prev => ({ ...prev, jobs: false }));
        return [];
      }
    };

    const fetchCampaigns = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const campaigns = await fetchDataWithRetry(`${process.env.REACT_APP_BASE_URL}/api/campaigns`, { headers });
        setData(prev => ({ ...prev, campaigns }));
        setLoadingStates(prev => ({ ...prev, campaigns: false }));
        return campaigns;
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setLoadingStates(prev => ({ ...prev, campaigns: false }));
        return [];
      }
    };

    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const events = await fetchDataWithRetry(`${process.env.REACT_APP_BASE_URL}/api/events/approved-events`, { headers });
        setData(prev => ({ ...prev, events }));
        setLoadingStates(prev => ({ ...prev, events: false }));
        return events;
      } catch (error) {
        console.error("Error fetching events:", error);
        setLoadingStates(prev => ({ ...prev, events: false }));
        return [];
      }
    };

    const fetchNewsletters = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const newsletters = await fetchDataWithRetry(`${process.env.REACT_APP_BASE_URL}/api/newsletter/published`, { headers });
        setData(prev => ({ ...prev, news: newsletters }));
        setLoadingStates(prev => ({ ...prev, newsletters: false }));
        return newsletters;
      } catch (error) {
        console.error("Error fetching newsletters:", error);
        setLoadingStates(prev => ({ ...prev, newsletters: false }));
        return [];
      }
    };
    const calculateStats = (alumni = [], jobs = [], campaigns = [], events = []) => {
      try {
        const uniqueCompanies = new Set(
          alumni
            .map(a => a.professionalInfo?.experiences?.find(e => e.isCurrent)?.company)
            .filter(Boolean)
        );

        const uniqueCountries = new Set(
          alumni
            .map(a => a.professionalInfo?.experiences?.find(e => e.isCurrent)?.location?.split(",").pop()?.trim())
            .filter(Boolean)
        );

        const uniqueCities = new Set(
          alumni
            .map(a => a.professionalInfo?.experiences?.find(e => e.isCurrent)?.location?.split(",")[0]?.trim())
            .filter(Boolean)
        );

        const salaries = jobs
          .map(j => j.jobDetails?.salary?.min && j.jobDetails?.salary?.max
            ? (j.jobDetails.salary.min + j.jobDetails.salary.max) / 2
            : null)
          .filter(Boolean);

        const avgSalary = salaries.length
          ? salaries.reduce((a, b) => a + b, 0) / salaries.length
          : 0;

        setStats({
          totalAlumni: alumni?.length || 0,
          verifiedAlumni: alumni?.filter(a => a.isVerified)?.length || 0,
          totalCompanies: uniqueCompanies.size,
          activeJobs: jobs?.filter(j => j.applicationDetails?.deadline && new Date(j.applicationDetails.deadline) > new Date())?.length || 0,
          totalRaised: campaigns?.reduce((sum, c) => sum + (c.raisedAmount || 0), 0) || 0,
          upcomingEvents: events?.filter(e => e.date && new Date(e.date) > new Date())?.length || 0,
          countries: uniqueCountries.size,
          cities: uniqueCities.size,
          totalDonors: campaigns?.reduce((sum, c) => sum + (c.donors?.length || 0), 0) || 0,
          averageSalary: avgSalary,
          referralJobs: jobs?.filter(j => j.referralEnabled)?.length || 0,
          successfulPlacements: Math.floor((alumni?.length || 0) * 0.85),
          mentorshipHours: (alumni?.length || 0) * 10,
          ongoingCampaigns: campaigns?.filter(c => c.timeline?.endDate && new Date(c.timeline.endDate) > new Date())?.length || 0,
        });
        
        setLoadingStates(prev => ({ ...prev, stats: false }));
      } catch (error) {
        console.error("Error calculating stats:", error);
        setLoadingStates(prev => ({ ...prev, stats: false }));
      }
    };

    // Start all fetches in parallel and update stats when all data is available
    Promise.all([
      fetchCurrentAlumni(),
      fetchAlumni(),
      fetchJobs(),
      fetchCampaigns(),
      fetchEvents(),
      fetchNewsletters(),
    ]).then(([alumni, jobs, campaigns, events]) => {
      calculateStats(alumni, jobs, campaigns, events);
      // Show confetti only when all data is loaded
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    }).catch(error => {
      console.error("Error fetching data:", error);
      setError("Some data failed to load. Please refresh to try again.");
    });
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="text-red-500 text-xl font-medium mb-4">{error}</div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 
              transition-colors duration-300"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  
  
  
  const FeatureSection = () => (
    <section className="relative py-20 bg-white">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Empower Your Professional Journey"
          subtitle="Discover powerful tools and resources designed to accelerate your career growth"
        />
        <EnhancedCarousel
          items={data.features}
          renderItem={(feature) => (
            <FeatureCard
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              color={feature.color}
              onClick={() => navigate(feature.path)}
            />
          )}
        />
      </div>
    </section>
  );

  const AlumniSection = () => (
    <section className="py-20 bg-white" id="alumnisection">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Explore Our Network"
          subtitle="Connect with our vibrant community members"
        />
        {loadingStates.alumni ? (
          <SectionLoadingAnimation />
        ) : (
          <>
            <EnhancedCarousel
              items={data.alumni
                .filter(alumnus => (
                  // Has profile photo (either avatar or personalInfo.avatar)
                  (alumnus.personalInfo?.avatar || alumnus.avatar) &&
                  // Currently employed (has experience marked as current)
                  alumnus.professionalInfo?.experiences?.some(exp => exp.isCurrent) &&
                  // Has skills listed
                  alumnus.professionalInfo?.skills?.length > 0
                ))
                .slice(0, 9)}
              renderItem={(alumnus) => (
                <AlumniCard key={alumnus._id} alumnus={alumnus} />
              )}
            />
            <motion.div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/alumni")}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                  text-white rounded-xl font-semibold shadow-lg hover:shadow-xl 
                  transition-all flex items-center space-x-2 mx-auto"
              >
                <Users className="w-5 h-5" />
                <span>View All Alumni</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );

  const JobsSection = () => (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Latest Opportunities"
          subtitle="Exclusive positions from top companies in your network"
        />
        {loadingStates.jobs ? (
          <SectionLoadingAnimation />
        ) : (
          <>
            <EnhancedCarousel
              items={data.jobs.slice(0, 9)}
              renderItem={(job) => (
                <JobCard
                  key={job._id}
                  job={{
                    id: job._id,
                    title: job.jobDetails.title,
                    companyName: job.company.name,
                    location: job.company.location,
                    type: job.jobDetails.type,
                    experience: `${job.jobDetails.experience.min}-${job.jobDetails.experience.max} years`,
                    jobDetails: { salary: job.jobDetails.salary },
                    skills: job.jobDetails.skills,
                    postedDate: formatDistanceToNow(new Date(job.createdAt), {
                      addSuffix: true,
                    }),
                    isRemote: job.jobDetails.isRemote,
                  }}
                  onViewDetails={() => navigate(`/jobs/${job._id}`)}
                />
              )}
            />
            <motion.div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/jobs")}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                  text-white rounded-xl font-semibold shadow-lg hover:shadow-xl 
                  transition-all flex items-center space-x-2 mx-auto"
              >
                <Briefcase className="w-5 h-5" />
                <span>Explore All Opportunities</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );

  const EventsSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Upcoming Events"
          subtitle="Join exciting events and connect with your community"
        />
        {loadingStates.events ? (
          <SectionLoadingAnimation />
        ) : (
          <>
            <EnhancedCarousel
              items={data.events.slice(0, 9)}
              renderItem={(event) => (
                <EventCard
                  key={event._id}
                  event={event}
                  onViewDetails={() => navigate(`/events/${event._id}`)}
                />
              )}
            />
            <motion.div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/events")}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                  text-white rounded-xl font-semibold shadow-lg hover:shadow-xl 
                  transition-all flex items-center space-x-2 mx-auto"
              >
                <Calendar className="w-5 h-5" />
                <span>View All Events</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );

  {/* Campaigns Section with Loading State */}
  const CampaignsSection = () => (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Active Campaigns"
          subtitle="Support meaningful initiatives in your community"
        />
        {loadingStates.campaigns ? (
          <SectionLoadingAnimation />
        ) : (
          <>
            <EnhancedCarousel
              items={data.campaigns.slice(0, 9)}
              renderItem={(campaign) => (
                <CampaignCard
                  key={campaign._id}
                  campaign={campaign}
                  onViewDetails={() => navigate(`/campaigns/${campaign._id}`)}
                />
              )}
            />
            <motion.div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/campaigns")}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                  text-white rounded-xl font-semibold shadow-lg hover:shadow-xl 
                  transition-all flex items-center space-x-2 mx-auto"
              >
                <Heart className="w-5 h-5" />
                <span>View All Campaigns</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );

  {/* Newsletter Section with Loading State */}
  const NewsletterSection = () => (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Latest Updates & Insights"
          subtitle="Stay informed with the latest news, stories, and achievements from our community"
        />
        {loadingStates.newsletters ? (
          <SectionLoadingAnimation />
        ) : (
          <>
            <EnhancedCarousel
              items={data.news.slice(0, 9)}
              renderItem={(newsletter) => (
                <NewsletterCard
                  key={newsletter._id}
                  newsletter={newsletter}
                  onViewDetails={() => navigate(`/newsletter/${newsletter._id}`)}
                />
              )}
            />
            <motion.div className="text-center mt-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/newsletters")}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                  text-white rounded-xl font-semibold shadow-lg hover:shadow-xl 
                  transition-all flex items-center space-x-2 mx-auto"
              >
                <FileText className="w-5 h-5" />
                <span>View All Newsletters</span>
              </motion.button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navbar/>
      {/* Hero Section */}
      {currentAlumniLoading ? (
      <LoadingAnimation />
    ) : currentAlumni ? (
      <HeroSection userData={currentAlumni} />
    ) : (
      <LoadingAnimation />
    )}      {/* <MySwiper /> */}

      <FeatureSection />
      <AlumniSection />
      <JobsSection />
      <EventsSection />
      <CampaignsSection />
      <NewsletterSection />

      {/* Updated FAQ Section */}
      <FaqSection />

      <section className="relative py-20 bg-gradient-to-br from-purple-800 via-indigo-800 to-blue-900 rounded-md overflow-hidden">
  <GeometricPatterns />
  <div className="relative max-w-7xl mx-auto px-4 text-center">
    <motion.h2
      className="text-4xl md:text-5xl font-bold text-white mb-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      Get In Touch With Us
    </motion.h2>
    <motion.p
      className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 }}
    >
      Have questions or need assistance? Our team is here to help you. Reach out to us and we'll get back to you as soon as possible.
    </motion.p>
    <motion.div
      className="flex flex-wrap justify-center gap-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.4 }}
    >
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/feedback")}
        className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all flex items-center space-x-3"
      >
        <Mail className="w-5 h-5" />
        <span>Contact Us</span>
      </motion.button>
     
    </motion.div>
  </div>
</section>
<Footer/>
    </div>
  );
};

export default Home;
